import React, { useEffect, useRef, useState } from 'react';
import HeroStatic from './HeroStatic';

/**
 * Skena 3D e hero-s.
 *
 * Three.js ngarkohet me `import()` pasi faqja është vizatuar, ndaj titulli dhe
 * butonat nuk e presin kurrë. Deri atëherë — dhe përgjithmonë kur WebGL mungon
 * ose dështon — rri pamja statike, që zë të njëjtin vend (pa kërcim faqosjeje).
 *
 * Animacioni ndalet kur skena del nga ekrani ose skeda fshihet; me
 * `prefers-reduced-motion` vizatohet një kuadër i vetëm i qetë. Butoni
 * «Ndalo» i jep vizitorit kontrollin mbi një lëvizje që zgjat mbi 5 sekonda.
 *
 * `t` janë tekstet e faqes në gjuhën aktuale. Shkrimet e ekraneve vizatohen një
 * herë në fillim, ndaj faqja e rimonton skenën (`key={lang}`) kur ndërrohet gjuha.
 */

export function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (_) {
    return false;
  }
}

const reduceQuery = () => (typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)')
  : null);

export default function HeroScene({ t }) {
  const hostRef = useRef(null);
  const handleRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | fallback
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(() => !!reduceQuery()?.matches);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !hasWebGL()) { setStatus('fallback'); return undefined; }

    let cancelled = false;
    const mq = reduceQuery();
    const mobile = window.matchMedia ? window.matchMedia('(max-width: 760px), (pointer: coarse)').matches : false;

    const fallback = () => {
      if (handleRef.current) { try { handleRef.current.dispose(); } catch (_) {} handleRef.current = null; }
      if (!cancelled) setStatus('fallback');
    };

    const boot = () => {
      import(/* webpackChunkName: "hero-scene" */ './heroScene3d')
        .then(({ createHeroScene }) => createHeroScene(host, {
          mobile,
          reducedMotion: !!mq?.matches,
          onError: fallback,
          texts: t.canvas,
        }))
        .then((handle) => {
          if (cancelled) { handle.dispose(); return; }
          handleRef.current = handle;
          if (process.env.NODE_ENV === 'development') window.__nxpHero = handle;
          handle.setPageVisible(!document.hidden);
          setStatus('ready');
        })
        .catch(fallback);
    };

    // Pret që faqja të vizatohet dhe shfletuesi të lirohet.
    const idle = window.requestIdleCallback
      ? window.requestIdleCallback(boot, { timeout: 1200 })
      : window.setTimeout(boot, 200);

    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([entry]) => handleRef.current?.setInView(entry.isIntersecting), { threshold: 0.05 })
      : null;
    io && io.observe(host);

    const onVisibility = () => handleRef.current?.setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);

    const onReduce = (e) => { setReduced(e.matches); handleRef.current?.setReducedMotion(e.matches); };
    if (mq) (mq.addEventListener ? mq.addEventListener('change', onReduce) : mq.addListener(onReduce));

    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && window.requestIdleCallback) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      io && io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      if (mq) (mq.removeEventListener ? mq.removeEventListener('change', onReduce) : mq.removeListener(onReduce));
      if (handleRef.current) { handleRef.current.dispose(); handleRef.current = null; }
    };
    // Tekstet lexohen vetëm në nisje; ndërrimi i gjuhës e rimonton komponentin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePause = () => {
    const next = !paused;
    setPaused(next);
    handleRef.current?.setPaused(next);
  };

  return (
    <div className="nxp-scene" data-status={status}>
      <div
        className="nxp-scene-stage"
        role="img"
        aria-label={t.scene.label}
      >
        <HeroStatic hidden={status === 'ready'} text={t.canvas} />
        <div ref={hostRef} className="nxp-scene-canvas" />
      </div>
      {status === 'ready' && !reduced && (
        <button type="button" className="nxp-scene-toggle" onClick={togglePause} aria-pressed={paused}>
          {paused ? (
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.5v9l7-4.5z" fill="currentColor" /></svg>
          ) : (
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.5h2v9H5zM9 3.5h2v9H9z" fill="currentColor" /></svg>
          )}
          <span>{paused ? t.scene.play : t.scene.pause}</span>
        </button>
      )}
    </div>
  );
}
